import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { Line } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend
} from 'chart.js';
import { TrackerSection } from '../../shared/TrackerSection';

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend
);

const EARLIEST_DATA_DATE = new Date(2025, 10, 1).getTime();
const BLS_COLOR = 'rgb(239, 68, 68)';

const TIME_RANGE_OPTIONS = [
  { value: 'all', label: 'All time' },
  { value: '6months', label: '6 mo' },
  { value: '3months', label: '3 mo' },
  { value: '1month', label: '1 mo' }
];

export const EggPriceTracker = ({ isDark, onLastUpdatedChange }) => {
  const [processedData, setProcessedData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [timeRange, setTimeRange] = useState('all');

  const apiUrl = process.env.REACT_APP_API_URL || '/api';

  const fetchData = useCallback(async () => {
    const maxRetries = 3;
    const retryDelay = 1000;
    for (let attempt = 1; attempt <= maxRetries; attempt++) {
      try {
        const response = await fetch(`${apiUrl}/prices?timeRange=${timeRange}`);
        if (!response.ok) throw new Error(`Server returned ${response.status}`);
        return await response.json();
      } catch (err) {
        if (attempt === maxRetries) throw err;
        await new Promise(resolve => setTimeout(resolve, retryDelay));
      }
    }
  }, [apiUrl, timeRange]);

  useEffect(() => {
    let cancelled = false;
    const loadData = async () => {
      setLoading(true);
      setError(null);
      try {
        const data = await fetchData();
        if (cancelled) return;
        const processed = data
          .map(item => ({
            date: new Date(item.date).toLocaleDateString('en-US', { timeZone: 'UTC' }),
            price: parseFloat(item.price),
            lastUpdated: item.lastUpdated,
            source: item.source.toLowerCase(),
            timestamp: new Date(item.date).getTime()
          }))
          .filter(item =>
            item.source === 'bls' &&
            Number.isFinite(item.price) &&
            item.timestamp >= EARLIEST_DATA_DATE
          )
          .sort((a, b) => a.timestamp - b.timestamp);
        setProcessedData(processed);
      } catch (err) {
        if (!cancelled) setError(err.message);
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    loadData();
    return () => { cancelled = true; };
  }, [fetchData]);

  const filteredData = useMemo(() => {
    if (timeRange === 'all') return processedData;
    const cutoffDate = new Date();
    switch (timeRange) {
      case '1month': cutoffDate.setMonth(cutoffDate.getMonth() - 1); break;
      case '3months': cutoffDate.setMonth(cutoffDate.getMonth() - 3); break;
      case '6months': cutoffDate.setMonth(cutoffDate.getMonth() - 6); break;
      default: return processedData;
    }
    return processedData.filter(item => item.timestamp >= cutoffDate.getTime());
  }, [timeRange, processedData]);

  const chartData = useMemo(() => ({
    labels: filteredData.map(item => item.date),
    datasets: [{
      label: 'BLS National Average',
      data: filteredData.map(item => ({ x: item.date, y: item.price })),
      borderColor: BLS_COLOR,
      backgroundColor: BLS_COLOR,
      tension: 0.1,
      pointRadius: 4,
      pointHoverRadius: 6
    }]
  }), [filteredData]);

  const chartOptions = useMemo(() => {
    const gridColor = isDark ? 'rgba(255, 255, 255, 0.1)' : 'rgba(0, 0, 0, 0.1)';
    const tickColor = isDark ? '#c4c4c8' : '#6b7280';
    return {
      responsive: true,
      maintainAspectRatio: true,
      aspectRatio: 2.6,
      interaction: { intersect: false, mode: 'index' },
      animation: { duration: 0 },
      hover: { animationDuration: 0 },
      responsiveAnimationDuration: 0,
      plugins: {
        tooltip: {
          enabled: true,
          animation: false,
          backgroundColor: isDark ? 'rgba(245, 245, 245, 0.95)' : 'rgba(17, 24, 39, 0.95)',
          titleColor: isDark ? '#0b0b0d' : '#f5f5f5',
          bodyColor: isDark ? '#0b0b0d' : '#f5f5f5',
          padding: 12,
          cornerRadius: 6,
          displayColors: true,
          callbacks: {
            label: (ctx) => `${ctx.dataset.label}: $${ctx.raw.y.toFixed(2)}`
          }
        },
        legend: { display: false },
        title: { display: false }
      },
      scales: {
        y: {
          beginAtZero: false,
          grid: { color: gridColor },
          ticks: {
            color: tickColor,
            callback: (value) => '$' + value.toFixed(2)
          }
        },
        x: {
          grid: { color: gridColor },
          ticks: {
            color: tickColor,
            maxRotation: 45,
            minRotation: 45
          }
        }
      }
    };
  }, [isDark]);

  const stats = useMemo(() => {
    const isLoaded = filteredData.length > 0;
    if (!isLoaded) {
      return [
        { label: 'Current', value: '—' },
        { label: 'Peak', value: '—' },
        { label: 'Change', value: '—' },
        { label: 'Source', value: 'BLS / Monthly' }
      ];
    }
    const prices = filteredData.map(d => d.price);
    const current = prices[prices.length - 1];
    const first = prices[0];
    const peak = Math.max(...prices);
    const pctChange = first ? ((current - first) / first) * 100 : 0;
    const trend = pctChange > 0.05 ? 'up' : pctChange < -0.05 ? 'down' : null;
    return [
      { label: 'Current', value: `$${current.toFixed(2)}` },
      { label: 'Peak', value: `$${peak.toFixed(2)}` },
      {
        label: 'Change',
        value: `${pctChange >= 0 ? '+' : ''}${pctChange.toFixed(1)}%`,
        trend
      },
      { label: 'Source', value: 'BLS / Monthly' }
    ];
  }, [filteredData]);

  useEffect(() => {
    if (processedData.length && onLastUpdatedChange) {
      const latest = processedData[processedData.length - 1];
      onLastUpdatedChange(latest.lastUpdated || latest.date);
    }
  }, [processedData, onLastUpdatedChange]);

  return (
    <TrackerSection
      label="EGGS"
      title="Grade A Large Eggs, per dozen"
      timeRange={timeRange}
      onTimeRangeChange={setTimeRange}
      timeRangeOptions={TIME_RANGE_OPTIONS}
      stats={stats}
      source="Bureau of Labor Statistics — series APU0000708111"
      cadence="Updated monthly"
      loading={loading}
      error={error}
    >
      <Line options={chartOptions} data={chartData} />
    </TrackerSection>
  );
};

export default EggPriceTracker;
